'use client';

import { Root, List, Trigger, Content } from '@radix-ui/react-tabs';
import { useRouter, usePathname } from 'next/navigation';
import { forwardRef, type ElementRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { Tooltip } from '@/components/ui/Tooltip';
import { Info } from 'lucide-react';

const Tabs = Root;

type TabItem = {
  value: string;
  href?: string;
  label: string;
  disabled?: boolean;
};

type TabsListProps = ComponentPropsWithoutRef<typeof List> & {
  items: TabItem[];
  defaultValue?: string;
  activeValue?: string;
  onValueChange?: (value: string) => void;
  tooltipContent?: ReactNode;
  tooltipLabel?: string;
};

const TabsList = forwardRef<ElementRef<typeof List>, TabsListProps>(
  (
    {
      className,
      items,
      defaultValue,
      activeValue,
      onValueChange,
      tooltipContent: tooltipText,
      tooltipLabel = 'Show tab guidance',
      ...props
    },
    ref
  ) => {
    const router = useRouter();
    const pathname = usePathname();

    const handleClick = (item: TabItem) => {
      if (item.href) {
        router.push(item.href);
      } else if (onValueChange) {
        onValueChange(item.value);
      }
    };

    const getActive = (item: TabItem) => {
      if (item.href) {
        return pathname === item.href;
      }

      return activeValue === item.value;
    };

    return (
      <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
        <List
          ref={ref}
          className={cn('flex min-w-max space-x-8 border-b border-gray-200 -mb-px', className)}
          {...props}
        >
          {items.map((item) => (
            <TabsTrigger
              key={item.value}
              value={item.value}
              onClick={() => handleClick(item)}
              data-active={getActive(item)}
              disabled={item.disabled}
            >
              {item.label}
            </TabsTrigger>
          ))}
          {tooltipText && (
            <div className="whitespace-nowrap px-1 py-4">
              <Tooltip maxWidth="max-w-[500px]" content={tooltipText}>
                <button
                  type="button"
                  aria-label={tooltipLabel}
                  className="inline-flex h-5 w-5 items-center justify-center text-primary"
                >
                  <Info className="h-5 w-5" aria-hidden="true" />
                </button>
              </Tooltip>
            </div>
          )}
        </List>
      </div>
    );
  }
);

TabsList.displayName = List.displayName;

const TabsTrigger = forwardRef<ElementRef<typeof Trigger>, ComponentPropsWithoutRef<typeof Trigger>>(
  ({ className, ...props }, ref) => (
    <Trigger
      ref={ref}
      className={cn(
        'whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium',
        'data-[active=true]:border-primary data-[active=true]:text-primary',
        'data-[active=false]:border-transparent data-[active=false]:text-gray-500',
        'hover:border-gray-300 hover:text-gray-700',
        className
      )}
      {...props}
    />
  )
);

TabsTrigger.displayName = Trigger.displayName;

const TabsContent = forwardRef<ElementRef<typeof Content>, ComponentPropsWithoutRef<typeof Content>>(
  ({ className, ...props }, ref) => <Content ref={ref} className={cn('mt-2', className)} {...props} />
);

TabsContent.displayName = Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
